/**
 * StepInstallGuide (Plan 11, Task 4 — FND-12 natural-action push).
 *
 * Shows the iOS Share → Add to Home Screen guide and the single "Skru på
 * varsler" button that calls `Notification.requestPermission()` ONLY on
 * the user's click — never on mount.  This is the whole point of FND-12:
 * the permission prompt happens at the natural moment the admin is
 * already thinking about notifications on their phone, not the second
 * they land on the dashboard.
 *
 * iOS CRIT-2 gate:  Classic Web Push on iOS requires the app to be
 * installed (`display-mode: standalone`).  When not standalone, the
 * button is rendered but aria-disabled with a tooltip telling the
 * admin to install first.  Browsers without the standalone constraint
 * (Android, desktop) still get the button live.
 *
 * Call order:  clicking "Ferdig":
 *   1. POST /api/onboarding/finish → familien er ferdig satt opp (FF-11:
 *      adminen er allerede logget inn med sin egen økt)
 *   2. IF Notification.permission === 'granted': subscribePush() runs
 *      with the session cookie (Plan 06 /subscribe requires auth)
 *   3. Oppfrisk økten (`family_setup_pending` er nå false) og navigate('/')
 *
 * #159 steg 9f: grønt glass, og Tilbake / Ferdig i `WizardFooter`.  Knappen
 * for varsler kaller fortsatt `Notification.requestPermission()` direkte i
 * klikket, uten noe `await` foran, så iOS ser det som en brukerhandling.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { SESSION_KEY } from '../../auth/useSession';
import { OnboardingApi } from '../api';
import { useOnboardingState } from '../useOnboardingState';
import { WizardFooter } from '../components/WizardFooter';
import { Button } from '../../../components/primitives/Button';
import { Card } from '../../../components/primitives/Card';

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

function readPermission(): PermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission as PermissionState;
}

function isInstalledPwa(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  try {
    return window.matchMedia('(display-mode: standalone)').matches;
  } catch {
    return false;
  }
}

export function StepInstallGuide() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setStep = useOnboardingState((s) => s.setStep);

  const standalone = useMemo(() => isInstalledPwa(), []);
  const [notifState, setNotifState] = useState<PermissionState>(() =>
    readPermission(),
  );
  const [finishing, setFinishing] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  // FND-12 guard — an earlier iteration of this plan auto-prompted on
  // mount, which is exactly what FND-12 forbids.  This effect is
  // intentionally empty; it documents the discipline, and a linter rule
  // would flag any `Notification.requestPermission` call in a useEffect.
  useEffect(() => {
    // no-op: do NOT call Notification.requestPermission() here (FND-12)
  }, []);

  async function onEnablePush() {
    if (typeof Notification === 'undefined') {
      setNotifState('unsupported');
      return;
    }
    const result = await Notification.requestPermission();
    setNotifState(result as PermissionState);
  }

  async function onFinish() {
    if (finishing) return;
    setFinishing(true);
    setApiError(null);
    try {
      await OnboardingApi.finish();
      if (notifState === 'granted') {
        const { subscribePush } = await import('../../../lib/push');
        try {
          await subscribePush('tablet');
        } catch {
          // Non-fatal: finish already succeeded.  Admin can re-enable
          // push from Settings (Plan 13) if this fails.
        }
      }
      await qc.invalidateQueries({ queryKey: SESSION_KEY });
      useOnboardingState.getState().reset();
      navigate('/', { replace: true });
    } catch (err) {
      setApiError(err instanceof Error ? err.message : 'unknown');
    } finally {
      setFinishing(false);
    }
  }

  function goBack() {
    setStep(2);
    navigate('/onboarding/step-2');
  }

  const pushButtonDisabled = notifState === 'granted' || notifState === 'unsupported';
  const pushButtonAriaDisabled = !standalone;

  return (
    <section data-testid="step-install-guide" className="flex flex-1 flex-col gap-5">
      <h1 className="m-0 text-heading-l font-extrabold tracking-tight text-ink">
        {t('onboarding.step4.title')}
      </h1>

      {/* Ikke aria-hidden lenger: dette er selve installasjonsveiledningen. */}
      <div data-testid="ios-frame-placeholder">
        <Card>
          <p className="m-0 text-body text-ink-2">{t('onboarding.step4.body')}</p>
        </Card>
      </div>

      <div className="flex flex-col gap-2">
        <Button
          data-testid="enable-push"
          variant="solid"
          size={52}
          disabled={pushButtonDisabled}
          aria-disabled={pushButtonDisabled || pushButtonAriaDisabled}
          title={!standalone ? t('onboarding.step4.install_first') : undefined}
          onClick={onEnablePush}
        >
          {t('onboarding.step4.enable_push')}
        </Button>

        {notifState === 'granted' ? (
          <p data-testid="push-granted" className="m-0 text-sm text-good">
            {t('onboarding.step4.push_granted')}
          </p>
        ) : null}
        {notifState === 'denied' ? (
          <p data-testid="push-denied" className="m-0 text-sm text-bad">
            {t('onboarding.step4.push_denied')}
          </p>
        ) : null}
      </div>

      {apiError ? (
        <p data-testid="finish-error" role="alert" className="m-0 text-sm text-bad">
          {t('onboarding.load_error')}
        </p>
      ) : null}

      <WizardFooter onBack={goBack} backDisabled={finishing}>
        <Button
          data-testid="finish"
          size={56}
          className="flex-[2]"
          disabled={finishing}
          onClick={onFinish}
        >
          {t('onboarding.step4.finish')}
        </Button>
      </WizardFooter>
    </section>
  );
}
